"use strict";

import InteractionView from "/js/interaction-views/interaction-view.js";
import Constants from "/js/core/constants.js";
import Global from "/js/core/global.js";
import AppUtil from "/js/util/app-util.js";
import AnimSequencer from "/js/util/anim-sequencer.js";
import DragDropItemView from "/js/interaction-views/dragdrop-item-view.js";
import FocusUtil from '../util/focus-util';

/**
 * Interaction with a stack of cards that can be drag-and-dropped into two
 * different buckets.
 */
export default class DragDropInteractionView extends InteractionView {

  // Used to prevent user from triggering overlapping card animations.
  static nextAllowedAnimTime = 0; // yea what

  constructor(data) {
    super(data);
    
    this._mode = data.mode;
    this._items = [];

    const s = `
        <div class="prompt">${ data.subCaption }</div>
        <div id="leftLabel" aria-hidden="true">${ data.leftText }</div>
        <div id="rightLabel" aria-hidden="true">${ data.rightText }</div>
        <div id="topBucket"><div class="inner"></div></div>
        <div id="leftBucket""><div class="bucketInner" tabindex="${ Constants.TABINDEX_CONTENT_GENERAL }" aria-label="${ data.leftText }"></div></div>
        <div id="rightBucket"><div class="bucketInner" tabindex="${ Constants.TABINDEX_CONTENT_GENERAL + 1 }" aria-label="${ data.rightText }"></div></div>
        <div id="dragDropCards"></div>
    `;
    this.$el.append($(s));

    this._$topBucket = this.$el.find("#topBucket");
    this._$leftBucket = this.$el.find("#leftBucket");
    this._$rightBucket = this.$el.find("#rightBucket");
    this._$cardsHolder = this.$el.find("#dragDropCards");

    // Init item views (cards).
    this.data.pieces.forEach(function(o, i) {
      const item = new DragDropItemView(o);
      this._$cardsHolder.prepend(item.$el);
      this._items.push(item);
    }, this);
    this.#updateTopStackCardsPositionClass(true);

    // Event listeners.
    this._$leftBucket.click(e => this.#onBucketClick(e));
    this._$rightBucket.click(e => this.#onBucketClick(e));
    FocusUtil.doStandardTreatment(this._$leftBucket.find(".bucketInner"), true);
    FocusUtil.doStandardTreatment(this._$rightBucket.find(".bucketInner"), true);

    this.$el.on("dragdrop-item-up", (e, o) => this.#onItemUp(e, o));
    this.$el.on("dragdrop-item-click", (e, item) => this.#onItemClick(e, item));

    this.resize();
  }

  resize() {
    const topBucketPos = this.#getTopBucketPos();
    const leftBucketPos = this.#getLeftBucketPos();
    const rightBucketPos = this.#getRightBucketPos();

    this._items.forEach(item => {
      
      // Position card.
      let pos;
      switch (item.currentBucket) {
        case "top":
          pos = topBucketPos;
          break;
        case "left":
          pos = leftBucketPos;
          break;
        case "right":
          pos = rightBucketPos;
          break;
        default:
          clw("WARNING: Check logic.");
          break;
      }
      AppUtil.setCssWithoutTransition(item.$el, () => { 
        item.$el.css(pos);
      });

      // Also tell card what the top bucket position is regardless (not ideal).
      item.setTopBucketPos(topBucketPos.left, topBucketPos.top)
    });
  }

  /* Returns an object with "left" and "top". */
  #getTopBucketPos() {
    const pos = this._$topBucket.position();
    pos.left /= Global.cardScale;  // !important
    pos.top /= Global.cardScale;
    return pos;
  }

  #getLeftBucketPos() {
    const pos = this._$leftBucket.position();
    pos.left /= Global.cardScale;
    pos.top /= Global.cardScale;
    return pos;
  }

  #getRightBucketPos() {
    const pos = this._$rightBucket.position();
    pos.left /= Global.cardScale;
    pos.top /= Global.cardScale;
    return pos;
  }

  /** Either moves card to target bucket or doesn't. */
  #doMoveCard(targetBucket) {
    if (targetBucket !== "left" && targetBucket !== "right") {
      return;
    }
    const card = this.#getTopCardInBucket("top");
    if (!card) {
      return;
    }
    if (new Date().getTime() < DragDropInteractionView.nextAllowedAnimTime) {
      return;
    }

    // Make sure card is above other cards.
    this._$cardsHolder.append(card.$el);

    if (this._mode === "correct-incorrect") {
      if (card.targetBucket === targetBucket) {
        // Is correct.
        this.#animateCard(card, targetBucket, true)
        this.#updateTopStackCardsPositionClass();
      } else {
        // Is incorrect.
        this.#animateCardAndBack(card, targetBucket)
      }
    } else {
      card.removePositionClass();
      this.#animateCard(card, targetBucket, false)
      this.#updateTopStackCardsPositionClass();
    }
  }

  #updateTopStackCardsPositionClass(dontAnimate=false) {
    let counter = 0;
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      if (item.currentBucket === "top") {
        item.setPositionClass(counter++, dontAnimate);
      }
    }
  }

  /* Animates card to target bucket. */
  #animateCard(card, targetBucket, andFlashCorrect) {

    DragDropInteractionView.nextAllowedAnimTime = new Date().getTime() + (andFlashCorrect ? 1000 : 500);

    card.currentBucket = targetBucket;

    const anim = () => {
      // Position.
      const destPos = (targetBucket === "left")
          ? this.#getLeftBucketPos() 
          : this.#getRightBucketPos();
      card.$el.css(destPos);
      // Scale.
      card.setPositionClass(-1);
    };

    const callback = () => {
      if (!andFlashCorrect) {
        this.#finishIfDone();
      } else {
        card.doFlash(true, () => { this.#finishIfDone(card); });
      }
    };

    AnimSequencer.runOne(card.$el, anim, callback);
  }

  /* Animates card to target bucket, flashes incorrect, and animates it back to the top bucket. */
  #animateCardAndBack(card, targetBucket) {

    DragDropInteractionView.nextAllowedAnimTime = new Date().getTime() + 1800;

    // 1. Move card to "bad" position.
    const seq = new AnimSequencer();
    seq.addAnim(card.$el, () => {
      const targetPos = (targetBucket === "left")
          ? this.#getLeftBucketPos() 
          : this.#getRightBucketPos();
      card.$el.css(targetPos);
    });
    // 2. Move card back to top position (but first do flash).
    seq.addAnim(card.$el, () => {
      card.doFlash(false, () => { 
        card.$el.css(this.#getTopBucketPos()); 
      });
    });
    seq.run();
  }

  #getTopCardInBucket(bucket) {
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      if (item.currentBucket === bucket) {
        return item;
      }
    }
    return null;
  }

  #finishIfDone() {
    if (this.#getTopCardInBucket("top")) { 
      return;
    }
    // Make the answers.
    this.answers = {
      interactionId: this.data.interactionId,
      type: "leftright",
      left: [],
      right: []
    };
    this._items.forEach((item, i) => {
      if (item.currentBucket === "left") {
        this.answers["left"].push(i);
      } else if (item.currentBucket === "right") {
        this.answers["right"].push(i);
      } else {
        clw("WARNING: bad bucket value:", item.currentBucket);
      }
    });
    cl('answers:', this.answers);
    this.finish();
  }

  #onBucketClick(e) { 
    // The click action represents an intention to move the current top card into either the left or
    //  right bucket.
    const s = (e.currentTarget === this._$leftBucket[0]) ? "left" : "right";
    this.#doMoveCard(s);
  }

  #onItemUp(e, o) {
    // Distance of released card from left bucket
    let dx = Math.abs(o.x - this.#getLeftBucketPos().left);
    let dy = Math.abs(o.y - this.#getLeftBucketPos().top);
    const distanceL = Math.hypot(dx, dy);

    // Distance of released card from right bucket
    dx = Math.abs(o.x - this.#getRightBucketPos().left);
    dy = Math.abs(o.y - this.#getRightBucketPos().top);
    const distanceR = Math.hypot(dx, dy);

    const threshold = this._items[0].contentWidth / 2;
    if (distanceL > threshold && distanceR > threshold) {
      // Put card back.
      o.card.$el.css(this.#getTopBucketPos()); 
    } else {
      const isLeftBucket = distanceL <= threshold;
      this.#doMoveCard(isLeftBucket ? "left" : "right");
    }
  }

  #onItemClick(e, item) {
    if (item.currentBucket === "top") {
      return;
    }
    this.#doMoveCard(item.currentBucket)
  }
}
